import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { FaceLandmarkerResult } from '@mediapipe/tasks-vision';

export type DistanceParams = {
  /** Interpupillary distance w milimetrach (średnio ~63 mm) */
  ipdMm?: number; // domyślnie 63
  /** Pole widzenia (szerokość) kamery w stopniach; często 60–70° dla laptopów */
  fovDeg?: number; // domyślnie 60
  /** Współczynnik wygładzania EMA (0.01–0.95) */
  emaAlpha?: number; // domyślnie 0.2
  /** Która kamera: frontowa ('user') lub tylna ('environment') */
  facingMode?: 'user' | 'environment'; // domyślnie 'user'
};

export type DistanceUpdate = {
  /** Szacowana odległość w cm (wygładzona EMA); null, jeśli brak twarzy */
  distanceCm: number | null;
  /** Surowy rozstaw "oczu" w pikselach (mediana z kilku par landmarków) */
  ipdPx: number | null;
  /** Czy wykryto twarz w tej klatce */
  hasFace: boolean;
  /** Rozmiar klatki wideo użyty do obliczeń */
  frameWidth: number;
  frameHeight: number;
  /** Czas (ms) z performance.now() */
  ts: number;
};

type Listener = (u: DistanceUpdate) => void;

export class DistanceTracker {
  private params: Required<DistanceParams> = {
    ipdMm: 63,
    fovDeg: 60,
    emaAlpha: 0.2,
    facingMode: 'user',
  };

  private landmarker: FaceLandmarker | null = null;
  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private running = false;
  private rafId: number | null = null;
  private emaVal: number | null = null;
  private listeners: Set<Listener> = new Set();

  // Landmarky do policzenia "rozstawu oczu" w px (Face Landmarker ma te indeksy)
  private static readonly EYE_PAIRS: [number, number][] = [
    [33, 263], // zewnętrzne kąciki oczu
    [133, 362], // wewnętrzne kąciki
    [159, 386], // górne powieki (fallback para)
  ];

  static instance: DistanceTracker;
  public static getInstance(opts?: DistanceParams) {
    if (!DistanceTracker.instance) {
      DistanceTracker.instance = new DistanceTracker(opts);
    }

    return DistanceTracker.instance;
  }

  constructor(opts?: DistanceParams) {
    if (opts) {
      this.setParams(opts);
    }
  }

  /** Zmień parametry działania w locie (IPD/FOV/próg/EMA/facingMode). */
  public setParams(opts: DistanceParams) {
    if (opts.ipdMm !== undefined) {
      this.params.ipdMm = opts.ipdMm;
    }
    if (opts.fovDeg !== undefined) {
      this.params.fovDeg = opts.fovDeg;
    }
    if (opts.emaAlpha !== undefined) {
      const a = Math.min(0.95, Math.max(0.01, opts.emaAlpha));
      this.params.emaAlpha = a;
    }
    if (opts.facingMode !== undefined) {
      this.params.facingMode = opts.facingMode;
    }
  }

  /** Subskrypcja aktualizacji dystansu zwraca funkcję do odsubskrybowania*/
  public subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  public getState() {
    return {
      running: this.running,
      params: { ...this.params },
      hasStream: !!this.stream,
      hasLandmarker: !!this.landmarker,
    };
  }

  public async start(): Promise<void> {
    if (this.running) {
      return;
    }

    if (!this.landmarker) {
      const files = await FilesetResolver.forVisionTasks(
        //'/node_modules/@mediapipe/tasks-vision/wasm',
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.7/wasm',
      );
      this.landmarker = await FaceLandmarker.createFromOptions(files, {
        baseOptions: {
          // dla npm: ścieżka do modelu w node_modules; dla CDN podaj pełny URL
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
        },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: false,
      });
    }

    // Kamera – ukryty element video w tle (nie dodajemy do DOM)
    if (!this.video) {
      this.video = document.createElement('video');
      this.video.autoplay = true;
      this.video.playsInline = true;
      this.video.muted = true; // i tak nie ma audio, ale wyciszamy
      // NIE dodajemy do DOM, więc podglądu brak
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: this.params.facingMode,
        width: { ideal: 960 },
        height: { ideal: 720 },
      },
      audio: false,
    });
    this.video.srcObject = this.stream;
    await this.video.play();

    this.running = true;
    this.loop();
  }

  /** Stop: zatrzymuje pętlę i zwalnia kamerę. */
  public stop() {
    this.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
    if (this.video) {
      this.video.srcObject = null;
      // nie usuwamy elementu; może być ponownie użyty przy kolejnym start
    }
    // nie kasujemy modelu; ponowny start będzie szybszy
    this.emaVal = null;
  }

  // pętla analizy
  private loop = () => {
    if (!this.running || !this.video || !this.landmarker) {
      return;
    }

    const ts = performance.now();
    const res = this.landmarker.detectForVideo(this.video, ts);

    let ipdPx: number | null = null;
    let distCm: number | null = null;

    if (res?.faceLandmarks?.length) {
      const lm = res.faceLandmarks[0];
      ipdPx = this.ipdPxFromLandmarks(
        lm,
        this.video.videoWidth,
        this.video.videoHeight,
      );
      distCm = this.estimateDistanceNoCal(ipdPx, this.video.videoWidth);
    }

    // emit
    const update: DistanceUpdate = {
      distanceCm: distCm,
      ipdPx: ipdPx,
      hasFace: !!res?.faceLandmarks?.length,
      frameWidth: this.video.videoWidth || 0,
      frameHeight: this.video.videoHeight || 0,
      ts,
    };
    this.listeners.forEach(fn => fn(update));

    this.rafId = requestAnimationFrame(this.loop);
  };

  private static deg2rad(d: number) {
    return (d * Math.PI) / 180;
  }

  private fpxFromFov(widthPx: number, fovDeg: number) {
    // f_px = (0.5 * W) / tan(FOV/2)
    const fov = DistanceTracker.deg2rad(
      Math.min(150, Math.max(20, fovDeg || 60)),
    );
    return (0.5 * widthPx) / Math.tan(fov / 2);
  }

  private estimateDistanceNoCal(
    ipdPx: number | null,
    widthPx: number,
  ): number | null {
    if (!ipdPx || !widthPx) {
      return null;
    }
    const ipd_cm = (this.params.ipdMm || 63) / 10; // mm -> cm
    const fpx = this.fpxFromFov(widthPx, this.params.fovDeg || 60);
    const d = (fpx * ipd_cm) / ipdPx;
    // EMA wygładzanie
    const a = this.params.emaAlpha;
    this.emaVal = this.emaVal === null ? d : a * d + (1 - a) * this.emaVal;
    return this.emaVal;
  }

  private ipdPxFromLandmarks(
    lm: NonNullable<FaceLandmarkerResult['faceLandmarks']>[number],
    w: number,
    h: number,
  ): number | null {
    const vals: number[] = [];
    for (const [i, j] of DistanceTracker.EYE_PAIRS) {
      const A = lm[i],
        B = lm[j];
      if (!A || !B) {
        continue;
      }
      const ax = A.x * w,
        ay = A.y * h;
      const bx = B.x * w,
        by = B.y * h;
      vals.push(Math.hypot(ax - bx, ay - by));
    }
    if (!vals.length) {
      return null;
    }
    vals.sort((a, b) => a - b);
    const m = Math.floor(vals.length / 2);
    return vals.length % 2 ? vals[m] : (vals[m - 1] + vals[m]) / 2;
  }
}

export default DistanceTracker;
